import { i18n } from '@quadralize/i18n'
import { Moon, Sun } from '@solar-icons/react'
import { AnimatePresence, motion } from 'framer-motion'
import { useTranslation } from 'react-i18next'

interface ThemeSwitcherProps {
  theme: 'light' | 'dark'
  onToggle: () => void
  className?: string
}

const ANIMATION_CONFIG = {
  button: {
    hoverScale: 1.05,
    tapScale: 0.95,
    springStiffness: 400,
    springDamping: 17,
  },
  icon: {
    slideDistance: 20,
    rotation: 90,
    duration: 0.2,
  },
  hoverBackground: {
    duration: 0.2,
  },
} as const

export function ThemeSwitcher({ theme, onToggle, className }: ThemeSwitcherProps) {
  const { t } = useTranslation('ui', { i18n })
  return (
    <motion.button
      type="button"
      onClick={onToggle}
      className={`flex h-10 w-10 items-center justify-center rounded-xl hover:bg-bg-muted text-text-secondary overflow-hidden group relative transition-colors ${className || ''}`}
      aria-label={t('themeSwitcher.ariaLabel')}
      whileHover={{ scale: ANIMATION_CONFIG.button.hoverScale }}
      whileTap={{ scale: ANIMATION_CONFIG.button.tapScale }}
      transition={{
        type: 'spring',
        stiffness: ANIMATION_CONFIG.button.springStiffness,
        damping: ANIMATION_CONFIG.button.springDamping,
      }}
    >
      <motion.div
        className="absolute inset-0 bg-primary/5"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: ANIMATION_CONFIG.hoverBackground.duration }}
      />

      <AnimatePresence mode="wait" initial={false}>
        {theme === 'light' ? (
          <motion.div
            key="moon"
            initial={{
              y: -ANIMATION_CONFIG.icon.slideDistance,
              opacity: 0,
              rotate: -ANIMATION_CONFIG.icon.rotation,
            }}
            animate={{ y: 0, opacity: 1, rotate: 0 }}
            exit={{
              y: ANIMATION_CONFIG.icon.slideDistance,
              opacity: 0,
              rotate: ANIMATION_CONFIG.icon.rotation,
            }}
            transition={{ duration: ANIMATION_CONFIG.icon.duration }}
          >
            <Moon weight="Bold" className="h-5 w-5 relative z-10" />
          </motion.div>
        ) : (
          <motion.div
            key="sun"
            initial={{
              y: -ANIMATION_CONFIG.icon.slideDistance,
              opacity: 0,
              rotate: -ANIMATION_CONFIG.icon.rotation,
            }}
            animate={{ y: 0, opacity: 1, rotate: 0 }}
            exit={{
              y: ANIMATION_CONFIG.icon.slideDistance,
              opacity: 0,
              rotate: ANIMATION_CONFIG.icon.rotation,
            }}
            transition={{ duration: ANIMATION_CONFIG.icon.duration }}
          >
            <Sun weight="Bold" className="h-5 w-5 relative z-10" />
          </motion.div>
        )}
      </AnimatePresence>
    </motion.button>
  )
}
